import React, { useState } from 'react';
import Card from '../ui/Card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DashboardFilters } from '../../pages/Dashboard';
const allChannelData = [{
  name: 'POS',
  baseCount: 45200,
  baseValue: 4850000000
}, {
  name: 'SoftPOS',
  baseCount: 12400,
  baseValue: 850000000
}, {
  name: 'Mobile POS',
  baseCount: 8900,
  baseValue: 620000000
}, {
  name: 'E-commerce',
  baseCount: 28500,
  baseValue: 5100000000
}, {
  name: 'RAAST QR',
  baseCount: 15600,
  baseValue: 450000000
}, {
  name: 'RAAST RTP',
  baseCount: 9800,
  baseValue: 700000000
}];
const timeMultipliers = {
  Today: 1,
  Week: 7,
  MTD: 22,
  YTD: 265
};
const schemeMultipliers: Record<string, number> = {
  'All Schemes': 1,
  Visa: 0.35,
  MasterCard: 0.28,
  PayPak: 0.2,
  UnionPay: 0.1,
  RAAST: 0.07
};
const channelMapping: Record<string, string[]> = {
  'All Channels': ['POS', 'SoftPOS', 'Mobile POS', 'E-commerce', 'RAAST QR', 'RAAST RTP'],
  POS: ['POS', 'SoftPOS', 'Mobile POS'],
  'E-commerce': ['E-commerce'],
  'RAAST QR': ['RAAST QR', 'RAAST RTP']
};
interface TransactionsByChannelProps {
  filters: DashboardFilters;
}
export const TransactionsByChannel: React.FC<TransactionsByChannelProps> = ({
  filters
}) => {
  const [activeTab, setActiveTab] = useState<'count' | 'value'>('count');
  const {
    timeRange,
    scheme,
    channel
  } = filters;
  const timeMult = timeMultipliers[timeRange];
  const schemeMult = schemeMultipliers[scheme];
  const allowedChannels = channelMapping[channel];
  const data = allChannelData.filter(ch => allowedChannels.includes(ch.name)).map(ch => ({
    name: ch.name,
    count: Math.round(ch.baseCount * timeMult * schemeMult),
    value: Math.round(ch.baseValue * timeMult * schemeMult)
  }));
  const formatYAxis = (val: number) => {
    if (activeTab === 'count') return new Intl.NumberFormat('en', {
      notation: 'compact'
    }).format(val);
    return new Intl.NumberFormat('en', {
      notation: 'compact',
      style: 'currency',
      currency: 'PKR'
    }).format(val);
  };
  const formatTooltip = (val: number) => {
    if (activeTab === 'count') return [new Intl.NumberFormat('en').format(val), 'Transactions'];
    return [new Intl.NumberFormat('en', {
      style: 'currency',
      currency: 'PKR',
      maximumFractionDigits: 0
    }).format(val), 'Value'];
  };
  return <Card data-id="element-292">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8" data-id="element-293">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight" data-id="element-294">
          Transaction Channels
        </h3>
        <div className="mt-4 sm:mt-0 flex p-1 bg-slate-100 dark:bg-slate-800/80 rounded-xl shadow-inner" data-id="element-295">
          <button onClick={() => setActiveTab('count')} className={`px-5 py-2 text-xs font-bold rounded-lg transition-all ${activeTab === 'count' ? 'bg-white dark:bg-slate-700 text-accent-600 dark:text-accent-400 shadow-sm ring-1 ring-slate-200/50 dark:ring-slate-600' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`} data-id="element-296">
            Volume
          </button>
          <button onClick={() => setActiveTab('value')} className={`px-5 py-2 text-xs font-bold rounded-lg transition-all ${activeTab === 'value' ? 'bg-white dark:bg-slate-700 text-accent-600 dark:text-accent-400 shadow-sm ring-1 ring-slate-200/50 dark:ring-slate-600' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`} data-id="element-297">
            Value
          </button>
        </div>
      </div>

      <div className="h-[320px] w-full" data-id="element-298">
        <ResponsiveContainer width="100%" height="100%" data-id="element-299">
          <BarChart data={data} margin={{
          top: 10,
          right: 10,
          left: 20,
          bottom: 20
        }} data-id="element-300">
            <defs data-id="element-301">
              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-302">
                <stop offset="0%" stopColor="#6366f1" data-id="element-303" />
                <stop offset="100%" stopColor="#4f46e5" data-id="element-304" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" className="dark:stroke-slate-800" data-id="element-305" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
            fontSize: 12,
            fill: '#64748b',
            fontWeight: 500
          }} dy={15} data-id="element-306" />
            <YAxis axisLine={false} tickLine={false} tick={{
            fontSize: 12,
            fill: '#64748b',
            fontWeight: 500
          }} tickFormatter={formatYAxis} dx={-10} data-id="element-307" />
            <Tooltip formatter={formatTooltip} cursor={{
            fill: 'rgba(241, 245, 249, 0.5)'
          }} contentStyle={{
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            fontWeight: 600,
            padding: '12px 16px'
          }} data-id="element-308" />
            <Bar dataKey={activeTab} fill="url(#barGradient)" radius={[6, 6, 0, 0]} barSize={48} data-id="element-309" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>;
};